import React, { Dispatch } from 'react';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import {
  Cached as CachedIcon,
  Done as DoneIcon,
  FileCopy as FileCopyIcon
} from '@mui/icons-material';
import { classes } from '../styles';
import { classes as aiStudioClasses } from '../../AIStudioModal/styles';
import { headerText } from '../config';
import dompurify from 'dompurify';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import SocialMediaPreview from './SocialMediaPreview';
import { SmartShareAction, SmartShareState } from '../reducer';
import { LoadingOverlay } from '../../AIStudioModal/components/LoadingOverlay';

function ResponseStep({
  generatedResponse,
  generatedKeyPoints,
  generatedResponseRef,
  generationType,
  jobUrl,
  generatePost,
  generateSummary,
  smartShareState,
  dispatch,
  handleCopy,
  handleRichCopy
}: {
  generatedResponse: string;
  generatedKeyPoints: string[];
  generatedResponseRef: React.RefObject<HTMLDivElement>;
  generationType: string;
  jobUrl: string;
  generatePost: (feedbackInput?: string) => void;
  generateSummary: (feedbackInput?: string) => void;
  smartShareState: SmartShareState;
  dispatch: Dispatch<SmartShareAction>;
  handleCopy: () => void;
  handleRichCopy: () => void;
}) {
  return (
    <>
      <Box sx={classes.responseHeader}>
        <Box sx={classes.responseHeaderText}>{`Your draft ${
          headerText[generationType as keyof typeof headerText]
        }`}</Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            id={`back-to-edit-fields-${smartShareState.generationType}`}
            sx={classes.footerBackButton}
            onClick={() => dispatch({ type: 'SET_CURRENT_STEP', payload: 0 })}
            disabled={smartShareState.isLoading}
          >
            Back
          </Button>
          <Tooltip placement="top" title={'Regenerate'}>
            <IconButton
              id={`regenerate-${generationType}`}
              onClick={
                generationType === 'advertisement'
                  ? () => generateSummary('')
                  : () => generatePost('')
              }
              sx={aiStudioClasses.actionButton}
              size="small"
              disabled={smartShareState.isLoading}
            >
              <CachedIcon />
            </IconButton>
          </Tooltip>
          {smartShareState.generationType === 'social' ? (
            <CopyToClipboard
              text={smartShareState.generatedResponse + `\n\n${jobUrl}`}
              onCopy={() => handleCopy()}
            >
              <Button
                id="copy-social-post-button"
                sx={classes.footerContinueButton}
                endIcon={smartShareState.copied ? <DoneIcon /> : <FileCopyIcon />}
                disabled={smartShareState.isLoading}
              >
                {smartShareState.copied ? 'Copied!' : 'Copy '}
              </Button>
            </CopyToClipboard>
          ) : (
            <Button
              id="copy-job-board-ad-button"
              sx={classes.footerContinueButton}
              endIcon={smartShareState.copied ? <DoneIcon /> : <FileCopyIcon />}
              onClick={handleRichCopy}
              disabled={smartShareState.isLoading}
            >
              {smartShareState.copied ? 'Copied!' : 'Copy'}
            </Button>
          )}
        </Stack>
      </Box>

      {generationType === 'advertisement' ? (
        <Box sx={classes.loadingOverlayContainer}>
          <Box
            sx={classes.generatedResponseContainer}
            ref={generatedResponseRef}
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(generatedResponse)
            }}
          />
          {generatedKeyPoints?.length > 0 && (
            <Box sx={{ width: '90%' }}>
              <Box sx={classes.fieldSubHeader}>Key selling points</Box>
              <Box>
                {generatedKeyPoints.map((point, index) => (
                  <Box key={index} sx={classes.keyPoint}>
                    {point}
                    <CopyToClipboard text={point}>
                      <IconButton disabled={smartShareState.isLoading} sx={classes.copyIcon}>
                        <FileCopyIcon />
                      </IconButton>
                    </CopyToClipboard>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <LoadingOverlay isLoading={smartShareState.isLoading} generationType={generationType} />
        </Box>
      ) : (
        <Box sx={classes.loadingOverlayContainer}>
          <Box sx={classes.socialResponseContainer}>
            <Box sx={classes.socialCard}>
              <Box sx={classes.socialCardHeader}>
                <span>{generatedResponse}</span>
                <a href={jobUrl}>{jobUrl}</a>
              </Box>
              <SocialMediaPreview jobUrl={jobUrl} />
            </Box>
          </Box>
          <LoadingOverlay isLoading={smartShareState.isLoading} generationType={generationType} />
        </Box>
      )}
    </>
  );
}

export default ResponseStep;
